import { useEffect, useCallback, useState } from 'react';
import { X, ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTour } from './useTour';

export function TourOverlay() {
  const { isRunning, activeTour, currentStep, endTour, nextStep, prevStep } = useTour();
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [spotlightRect, setSpotlightRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const step = activeTour?.steps[currentStep];

  const positionTooltip = useCallback(() => {
    if (!step) return;

    if (step.placement === 'center' || step.target === 'body') {
      setSpotlightRect(null);
      setTooltipPos({
        top: window.innerHeight / 2 - 120,
        left: window.innerWidth / 2 - 200,
      });
      return;
    }

    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      const padding = 8;
      setSpotlightRect({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
      setTooltipPos({
        top: Math.min(rect.bottom + 12, window.innerHeight - 250),
        left: Math.min(Math.max(rect.left, 16), window.innerWidth - 416),
      });
    } else {
      // Target not found — fall back to centered
      setSpotlightRect(null);
      setTooltipPos({
        top: window.innerHeight / 2 - 120,
        left: window.innerWidth / 2 - 200,
      });
    }
  }, [step]);

  // Scroll target into view and position tooltip when step changes
  useEffect(() => {
    if (!isRunning || !step) return;

    if (step.target !== 'body' && step.placement !== 'center') {
      const el = document.querySelector(step.target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Wait for scroll to finish before positioning
        const timer = setTimeout(positionTooltip, 350);
        return () => clearTimeout(timer);
      }
    }
    positionTooltip();
  }, [isRunning, currentStep, positionTooltip, step]);

  // Reposition on window resize
  useEffect(() => {
    if (!isRunning) return;
    const handleResize = () => positionTooltip();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isRunning, positionTooltip]);

  if (!isRunning || !activeTour || !step) return null;

  const isLast = currentStep === activeTour.steps.length - 1;

  return (
    <>
      {/* Overlay with spotlight cutout */}
      {spotlightRect ? (
        <div
          className="fixed z-[9998] rounded-lg transition-all duration-300 ease-in-out"
          style={{
            top: `${spotlightRect.top}px`,
            left: `${spotlightRect.left}px`,
            width: `${spotlightRect.width}px`,
            height: `${spotlightRect.height}px`,
            boxShadow:
              '0 0 0 9999px rgba(0, 0, 0, 0.55), 0 0 0 3px rgba(99, 102, 241, 0.7)',
          }}
          onClick={endTour}
        />
      ) : (
        <div className="fixed inset-0 bg-black/55 z-[9998]" onClick={endTour} />
      )}

      {/* Tooltip card */}
      <Card
        className="fixed z-[10000] w-[400px] shadow-xl border-0"
        style={{
          top: `${tooltipPos.top}px`,
          left: `${tooltipPos.left}px`,
        }}
      >
        <CardHeader className="pb-2 flex flex-row items-start justify-between">
          <CardTitle className="text-base font-semibold text-foreground">
            {step.title}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 -mt-1 -mr-2"
            onClick={endTour}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {step.content}
          </p>
          <div className="flex items-center justify-between">
            <button
              onClick={endTour}
              className="text-xs text-muted-foreground hover:text-muted-foreground transition-colors"
            >
              <SkipForward className="h-3 w-3 inline mr-1" />
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {currentStep + 1} / {activeTour.steps.length}
              </span>
              {currentStep > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prevStep}
                  className="text-muted-foreground"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={nextStep}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {isLast ? 'Finish' : 'Next'}
                {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
